'use strict';

import React, { useState, useEffect, useRef } from 'react';
import * as ReactDOMClient from 'react-dom/client';
import { gfrAdult, gfrChild } from './gfr-calcs.js';

export const GFR = (props) => {
    const { adult, discSource } = props,
        [results, setResults] = useState(false),
        [isConv, setIsConv] = useState(true),
        [isFemale, setIsFemale] = useState(''),
        [isFemaleInvld, setIsFemaleInvld] = useState(false),
        [age, setAge] = useState(''),
        [isMetric, setIsMetric] = useState(false),
        [scr, setScr] = useState(''),
        [scy, setScy] = useState(''),
        [scrReq, setScrReq] = useState(true),
        [scyReq, setScyReq] = useState(true),
        [cm, setCm] = useState(''),
        [feet, setFeet] = useState(''),
        [inch, setInch] = useState(''),
        [scrOut, setScrOut] = useState(''),
        [scyOut, setScyOut] = useState(''),
        [bothOut, setBothOut] = useState(''),
        isAdult = adult !== 'child',
        disc = useRef(null),
        idSuffix = Math.abs(
            new Date().valueOf() ^ Math.floor(Math.random() * 1e9)
        ).toString(36),
        ageMin = isAdult ? 18 : 1,
        ageMax = isAdult ? 200 : 25,
        scrMinSi = (isConv ? 0.2 : 0.2 * 88.4).toFixed(2),
        scrMaxSi = (isConv ? 20 : 20 * 88.4).toFixed(2),
        showResults = () => setResults(true),
        startOver = () => {
            setResults(false);
            setIsConv(true);
            setIsMetric(false);
            setIsFemale('');
            setAge('');
            setScr('');
            setScy('');
            setCm('');
            setFeet('');
            setInch('');
        },
        checkValues = () => {
            const scrParse = parseFloat(scr),
                scyParse = parseFloat(scy);
            return (
                (!scyParse && scrParse >= scrMinSi && scrParse <= scrMaxSi) ||
                (!scrParse && scyParse >= 0.001) ||
                (scrParse >= scrMinSi &&
                    scrParse <= scrMaxSi &&
                    scyParse >= 0.001)
            );
        },
        requiredCheckAdult = () => {
            const ageParse = parseFloat(age);
            return isFemale !== '' &&
                ageParse >= ageMin &&
                ageParse <= ageMax &&
                checkValues()
                ? true
                : false;
        },
        requiredCheckChild = () => {
            const ageParse = parseFloat(age);
            return isFemale !== '' &&
                ageParse >= ageMin &&
                ageParse <= ageMax &&
                (parseFloat(cm) >= 0.1 || parseFloat(feet)) &&
                checkValues()
                ? true
                : false;
        };

    useEffect(() => {
        disc.current.appendChild(discSource);
    }, [adult]);

    useEffect(() => {
        const scrParse = parseFloat(scr),
            scyParse = parseFloat(scy);
        if (scrParse) setScyReq(false);
        else if (scyParse) setScrReq(false);
        else if (!scrParse && !scyParse) {
            setScyReq(true);
            setScrReq(true);
        }
    }, [scr, scy]);

    useEffect(() => {
        setCm('');
        setFeet('');
        setInch('');
    }, [isMetric]);

    useEffect(() => {
        const scrParse = parseFloat(scr),
            scyParse = parseFloat(scy),
            ageParse = parseFloat(age),
            cmParse = parseFloat(cm),
            feetParse = parseFloat(feet),
            inchParse = parseFloat(inch);
        if (results) {
            if (scrParse)
                setScrOut(
                    isAdult
                        ? gfrAdult({
                              isConv,
                              isFemale,
                              age: ageParse,
                              scr: scrParse,
                              scy: 0
                          })
                        : gfrChild({
                              isConv,
                              isFemale,
                              age: ageParse,
                              cm: cmParse,
                              feet: feetParse,
                              inch: inchParse,
                              scr: scrParse,
                              scy: 0
                          })
                );
            if (scyParse)
                setScyOut(
                    isAdult
                        ? gfrAdult({
                              isConv,
                              isFemale,
                              age: ageParse,
                              scr: 0,
                              scy: scyParse
                          })
                        : gfrChild({
                              isConv,
                              isFemale,
                              age: ageParse,
                              cm: cmParse,
                              feet: feetParse,
                              inch: inchParse,
                              scr: 0,
                              scy: scyParse
                          })
                );
            if (scrParse && scyParse)
                setBothOut(
                    isAdult
                        ? gfrAdult({
                              isConv,
                              isFemale,
                              age: ageParse,
                              scr: scrParse,
                              scy: scyParse
                          })
                        : gfrChild({
                              isConv,
                              isFemale,
                              age: ageParse,
                              cm: cmParse,
                              feet: feetParse,
                              inch: inchParse,
                              scr: scrParse,
                              scy: scyParse
                          })
                );
            (window.dataLayer = window.dataLayer || []).push({
                event: 'eGFRCalculator',
                eGFRCalculator: {
                    name: isAdult
                        ? 'eGFR Calculator for Adults'
                        : 'eGFR Calculator for Children'
                }
            });
        }
    }, [results]);

    return (
        <form id={'gfr-form' + idSuffix} className="dk-form gfr-calculator v2">
            <div className="heading4">
                Estimated Glomerular Filtration Rate (eGFR) Calculator for{' '}
                {isAdult ? 'Adults' : 'Children'}
            </div>
            {!results ? (
                <>
                    <div className="gfr-subheading">
                        Fields marked with asterisks (*) are required.
                    </div>
                    <div className="field options stacked">
                        <span className="label">Measurement Unit</span>
                        <div className="group">
                            <div>
                                <input
                                    type="radio"
                                    id={'measurement_conv-' + idSuffix}
                                    name="measurement"
                                    value="conventional"
                                    checked={isConv}
                                    onChange={() => setIsConv(true)}
                                />
                                <label htmlFor={'measurement_conv-' + idSuffix}>
                                    Conventional (mg/dL)
                                </label>
                            </div>
                            <div>
                                <input
                                    type="radio"
                                    id={'measurement_si-' + idSuffix}
                                    name="measurement"
                                    value="si"
                                    checked={!isConv}
                                    onChange={() => setIsConv(false)}
                                />
                                <label htmlFor={'measurement_si-' + idSuffix}>
                                    SI (µmol/L)
                                </label>
                            </div>
                        </div>
                    </div>
                    <div
                        id={'creaCysDesc-' + idSuffix}
                        className="gfr-instruction"
                    >
                        Enter Creatinine or Cystatin C or <strong>both</strong>
                    </div>
                    <div
                        className={
                            'field text hide-req' + (scrReq ? ' required' : '')
                        }
                        aria-describedby={'creaCysDesc-' + idSuffix}
                    >
                        <label htmlFor={'scr-' + idSuffix}>
                            Serum creatinine {isConv ? '(mg/dL)' : '(µmol/L)'}
                        </label>
                        <input
                            type="number"
                            id={'scr-' + idSuffix}
                            name="scr"
                            autoComplete="off"
                            pattern="^(?:\d*\.\d*|\d+)$"
                            step="0.001"
                            min={(isConv ? 0.2 : 0.2 * 88.4).toFixed(2)}
                            max={(isConv ? 20 : 20 * 88.4).toFixed(2)}
                            onKeyDown={(e) => {
                                const inv = ['e', 'E', '-', '+'];
                                if (inv.includes(e.key)) e.preventDefault();
                            }}
                            onChange={(e) => setScr(e.target.value)}
                            required={scrReq}
                        />
                        <div className="validation">
                            Enter a numeric value of at least 0.001
                        </div>
                    </div>
                    <div
                        className={
                            'field text hide-req' + (scyReq ? ' required' : '')
                        }
                        aria-describedby={'creaCysDesc-' + idSuffix}
                    >
                        <label htmlFor={'scy-' + idSuffix}>
                            Serum cystatin C (mg/L)
                        </label>
                        <input
                            type="number"
                            id={'scy-' + idSuffix}
                            name="scy"
                            autoComplete="off"
                            pattern="^(?:\d*\.\d*|\d+)$"
                            step="0.001"
                            min="0.001"
                            onKeyDown={(e) => {
                                const inv = ['e', 'E', '-', '+'];
                                if (inv.includes(e.key)) e.preventDefault();
                            }}
                            onChange={(e) => setScy(e.target.value)}
                            required={scyReq}
                        />
                        <div className="validation">
                            Enter a numeric value of at least 0.001
                        </div>
                    </div>
                    <div
                        className={
                            'field options stacked required' +
                            (isFemaleInvld ? ' invalid' : '')
                        }
                    >
                        <span className="label">Sex</span>
                        <div className="group">
                            <div>
                                <input
                                    type="radio"
                                    id={'sex_female-' + idSuffix}
                                    name="sex"
                                    value="Female"
                                    checked={isFemale && isFemale !== ''}
                                    onChange={() => {
                                        setIsFemale(true);
                                        if (isFemaleInvld)
                                            setIsFemaleInvld(false);
                                    }}
                                    required
                                />
                                <label htmlFor={'sex_female-' + idSuffix}>
                                    Female
                                </label>
                            </div>
                            <div>
                                <input
                                    type="radio"
                                    id={'sex_male-' + idSuffix}
                                    name="sex"
                                    value="Male"
                                    checked={!isFemale && isFemale !== ''}
                                    onChange={() => {
                                        setIsFemale(false);
                                        if (isFemaleInvld)
                                            setIsFemaleInvld(false);
                                    }}
                                />
                                <label htmlFor={'sex_male-' + idSuffix}>
                                    Male
                                </label>
                            </div>
                        </div>
                        <div className="validation">
                            Select a sex{' '}
                            <i className="i-exclamation-circle"></i>
                        </div>
                    </div>
                    <div className="field text required">
                        <label htmlFor={'age-' + idSuffix}>
                            Age {!isAdult && '(years)'}
                        </label>
                        <input
                            type="number"
                            id={'age-' + idSuffix}
                            name="age"
                            required
                            autoComplete="off"
                            pattern="^\d+\.?\d*$"
                            min={ageMin}
                            max={ageMax}
                            onKeyDown={(e) => {
                                const inv = ['e', 'E', '-', '+', '.'];
                                if (inv.includes(e.key)) e.preventDefault();
                            }}
                            onChange={(e) => setAge(e.target.value)}
                        />
                        <div className="validation">
                            {'Enter a numeric age' +
                                (isAdult ? ' 18 or over' : ' 25 or under')}
                        </div>
                    </div>
                    {!isAdult ? (
                        <div className="field options stacked">
                            <span className="label">
                                Measurement unit for height
                            </span>
                            <div className="group">
                                <div>
                                    <input
                                        type="radio"
                                        id={'metric_false-' + idSuffix}
                                        name="metric"
                                        value="metric"
                                        checked={!isMetric}
                                        onChange={() => setIsMetric(false)}
                                    />
                                    <label htmlFor={'metric_false-' + idSuffix}>
                                        feet/inches
                                    </label>
                                </div>
                                <div>
                                    <input
                                        type="radio"
                                        id={'metric_true-' + idSuffix}
                                        name="metric"
                                        value="imperial"
                                        checked={isMetric}
                                        onChange={() => setIsMetric(true)}
                                    />
                                    <label htmlFor={'metric_true-' + idSuffix}>
                                        centimeters
                                    </label>
                                </div>
                            </div>
                        </div>
                    ) : null}
                    {!isAdult && isMetric ? (
                        <div className="field text required">
                            <label htmlFor={'cm-' + idSuffix}>
                                Height (cm)
                            </label>
                            <input
                                type="number"
                                id={'cm-' + idSuffix}
                                name="height"
                                required
                                autoComplete="off"
                                pattern="^(?:\d*\.\d*|\d+)$"
                                step="0.1"
                                min="0.1"
                                onKeyDown={(e) => {
                                    const inv = ['e', 'E', '-', '+'];
                                    if (inv.includes(e.key)) e.preventDefault();
                                }}
                                onChange={(e) => setCm(e.target.value)}
                            />
                            <div className="validation">
                                Enter a numeric height of at least 0.1
                                centimeters (cm)
                            </div>
                        </div>
                    ) : !isAdult && !isMetric ? (
                        <div className="field text dropdown-group required">
                            <div className="dropdown">
                                <label htmlFor={'feet-' + idSuffix}>
                                    Height
                                    <span className="sr-only"> in feet</span>
                                </label>
                                <select
                                    id={'feet-' + idSuffix}
                                    className={
                                        feet === ''
                                            ? 'dropdown-placeholder'
                                            : ''
                                    }
                                    name="feet"
                                    required
                                    defaultValue={''}
                                    onChange={(e) => setFeet(e.target.value)}
                                >
                                    <option value={''} disabled>
                                        Feet
                                    </option>
                                    ...
                                    {['1', '2', '3', '4', '5', '6', '7'].map(
                                        (opt) => (
                                            <option key={opt} value={opt}>
                                                {opt} ft
                                            </option>
                                        )
                                    )}
                                </select>
                            </div>
                            <div className="dropdown">
                                <label htmlFor={'inches-' + idSuffix}>
                                    Inches
                                </label>
                                <select
                                    id={'inches-' + idSuffix}
                                    className={
                                        inch === ''
                                            ? 'dropdown-placeholder'
                                            : ''
                                    }
                                    name="inches"
                                    defaultValue={''}
                                    onChange={(e) => setInch(e.target.value)}
                                >
                                    <option value={''} disabled>
                                        Inches
                                    </option>
                                    ...
                                    {[
                                        '0',
                                        '1',
                                        '2',
                                        '3',
                                        '4',
                                        '5',
                                        '6',
                                        '7',
                                        '8',
                                        '9',
                                        '10',
                                        '11'
                                    ].map((opt) => (
                                        <option key={opt} value={opt}>
                                            {opt} in
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>
                    ) : null}
                    <div className="buttons">
                        <button
                            type="submit"
                            className="large blue"
                            onClick={() => {
                                if (isFemale === '') {
                                    setIsFemaleInvld(true);
                                } else if (
                                    isAdult
                                        ? requiredCheckAdult()
                                        : requiredCheckChild()
                                ) {
                                    showResults();
                                }
                            }}
                        >
                            Calculate
                        </button>
                    </div>
                </>
            ) : (
                <>
                    <div className="heading3">
                        Equation Result{scr && scy ? 's' : ''}
                    </div>
                    {scr && scy ? (
                        <div className="gfr-result centered highlighted">
                            <div className="heading4">
                                {isAdult ? 'CKD-EPI' : 'U25'} Creatinine and
                                Cystatin C
                            </div>
                            <div className="gfr-result-value">
                                <span>{bothOut} </span>
                                <span>mL/min/1.73 m²</span>
                            </div>
                        </div>
                    ) : null}
                    {scr ? (
                        <div
                            className={'gfr-result' + (!scy ? ' centered' : '')}
                        >
                            <div className="heading4">
                                {isAdult ? 'CKD-EPI' : 'U25'} Creatinine
                            </div>
                            <div className="gfr-result-value">
                                <span>{scrOut} </span>
                                <span>mL/min/1.73 m²</span>
                            </div>
                        </div>
                    ) : null}
                    {scy ? (
                        <div
                            className={'gfr-result' + (!scr ? ' centered' : '')}
                        >
                            <div className="heading4">
                                {isAdult ? 'CKD-EPI' : 'U25'} Cystatin C
                            </div>
                            <div className="gfr-result-value">
                                <span>{scyOut} </span>
                                <span>mL/min/1.73 m²</span>
                            </div>
                        </div>
                    ) : null}
                    <div className="gfr-stats">
                        {parseFloat(scr) ? (
                            <span>
                                Creatinine {scr}{' '}
                                {isConv ? '(mg/dL)' : '(µmol/L)'}
                            </span>
                        ) : null}
                        {parseFloat(scy) ? (
                            <span>Cystatin {scy} (mg/L)</span>
                        ) : null}
                        <span>{isFemale ? 'Female' : 'Male'}</span>
                        <span>{age} years old</span>
                        {!isAdult ? (
                            <span>
                                Height{' '}
                                {cm ? `${cm}cm` : `${feet}'${inch || 0}"`}
                            </span>
                        ) : null}
                    </div>
                    <div className="buttons">
                        <button
                            type="button"
                            className={
                                'large blue' + (results ? ' centered' : '')
                            }
                            onClick={() => startOver()}
                        >
                            Start Over
                        </button>
                    </div>
                </>
            )}
            <div className="gfr-disclaimer-container" ref={disc}></div>
        </form>
    );
};

if (!window.dk.isEditing) {
    const roots = document.querySelectorAll('.gfr-root');
    roots.forEach((r) => {
        const adult = r.getAttribute('data-gfr-type'),
            discSource = r.querySelector('.gfr-disclaimer'),
            root = ReactDOMClient.createRoot(r);
        discSource.remove();
        root.render(<GFR adult={adult} discSource={discSource} />);
    });
}


